import * as React from 'react';

import * as analytics from '@opendoor/shared-fe/build/analytics';
import type { IVideo } from '@opendoor/bricks/complex/VideoPlayer/VideoPlayer';
import { Box, Flex, H2, Link, Text } from '@opendoor/bricks/core';

import imgixUrl from '../../helpers/imgixUrl';

const VideoPlayer = React.lazy(() => import('@opendoor/bricks/complex/VideoPlayer/VideoPlayer'));

const FAQSection: React.FC = (): JSX.Element => {
  const whatHappensNext = {
    icon: '00a2573b92f545e4a4681798f03665c4.svg',
    description:
      'Schedule a call with your dedicated Experience Partner, who can answer any questions you have… →',
    title: 'What happens next in the process?',
    URL: 'https://www.opendoor.com/w/faq/happens-after-request-offer/',
  };
  const comeUpWithOffer = {
    icon: 'd1b135b521ed48e88c53be07d60e21c4.svg',
    description: 'To calculate a fair market offer for your home, we take into… →',
    title: 'How does Opendoor come up with my offer amount?',
    URL: 'https://www.opendoor.com/w/faq/how-do-you-determine-the-offer-price-for-my-home/',
  };
  const whatIfIForgot = {
    icon: 'd1409686722b49728818ef070a511f04.svg',
    description: 'Our goal is to give you the most accurate offer we can… →',
    title: 'What if I forgot to include information?',
    URL: 'https://www.opendoor.com/w/faq/forgot-include-information-home/',
  };
  const costCompareVideo = {
    thumbnailUrl:
      'https://i.vimeocdn.com/video/810204002-230e5059a33e9e904e74e68d8988b30d4c57772e25e9c91d6cbae019e96fb026-d?mw=1920&mh=1080&q=70',
    title: 'How to book free tours for any home on the market on your schedule',
    videoUrl: 'https://vimeo.com/356976574',
    durationMinutes: 2,
  };

  const trackVideoToggle = (video: IVideo, action: 'open' | 'close') => {
    analytics.trackEvent('flow-complete', 'video', action, { ...video });
  };

  const FAQs: Array<{
    icon: string;
    description: string;
    title: string;
    URL: string;
  }> = [
    whatHappensNext,
    comeUpWithOffer,
    {
      icon: '1263f0d634284cdca383af24449da175.svg',
      description:
        'Opendoor typically makes an offer within 1-2 business days without needing to send someone… →',
      title: 'Does anyone come out to value it?',
      URL: 'https://www.opendoor.com/w/faq/does-someone-come-value-home/',
    },
    {
      icon: '5f52bd3ebdfb4e0f80711e767c33c2df.svg',
      description: 'We want you to feel confident about your home sale, so we’ve… →',
      title: 'What is an offer review and why do I need one?',
      URL: 'https://www.opendoor.com/w/faq/offer-review-need-one/',
    },
    whatIfIForgot,
    {
      icon: 'c8ed8c951e634a529efe23c5e985fc67.svg',
      description: 'We look for items that are… →',
      title: 'What types of repairs do you look for during the home condition assessment?',
      URL: 'https://www.opendoor.com/w/faq/what-types-of-repairs-does-opendoor-charge-for/',
    },
  ];

  return (
    <Box as="section" pt={0} pb={0}>
      <Box mt="80px" mb="60px" borderTop={`1px solid var(--colors-neutrals30)`} />
      <Box mb={9} textAlign="center">
        <H2>Frequently asked questions</H2>
        <Box maxWidth="960px" mt={9} mx="auto">
          <Flex justifyContent="center" flexWrap="wrap">
            <Box p={4} width="100%" minWidth="320px" textAlign="left">
              <Box width="50%" margin="auto">
                <React.Suspense fallback={null}>
                  <VideoPlayer
                    display="card"
                    video={costCompareVideo}
                    onModalToggled={trackVideoToggle}
                    analyticsName="reception-fe-faq-cost-compare-video-player"
                  />
                </React.Suspense>
              </Box>
            </Box>
            {FAQs.map((faq) => (
              <Link
                href={faq.URL}
                key={faq.title}
                display="flex"
                width="50%"
                minWidth="340px"
                analyticsName="reception-fe-faq"
                aria-label=""
              >
                <Flex
                  m={4}
                  px={[6, null, 8]}
                  py={[7, null, 8]}
                  flexDirection="row"
                  border={`1px solid var(--colors-neutrals30)`}
                  borderRadius="squared"
                  transition="0.3s ease"
                  _hover={{
                    boxShadow: '0 0 35px -5px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Box
                    minWidth="50px"
                    mt={2}
                    mr={5}
                    backgroundRepeat="no-repeat"
                    backgroundPosition="top"
                    backgroundImage={`url(${imgixUrl(faq.icon)})`}
                  />
                  <Flex
                    width="100%"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    textAlign="left"
                  >
                    {/* eslint-disable-next-line react/jsx-pascal-case */}
                    <Text
                      fontSize="s1"
                      lineHeight="s0"
                      mb={2}
                      fontWeight="semibold"
                      color="neutrals100"
                    >
                      {faq.title}
                    </Text>
                    {/* eslint-disable-next-line react/jsx-pascal-case */}
                    <Text fontSize="s0" lineHeight="s00" mb={2} color="neutrals70">
                      {faq.description}
                    </Text>
                  </Flex>
                </Flex>
              </Link>
            ))}
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};
export default FAQSection;
